/**
 * Article Service - Supabase CRUD operations for shared articles
 * 
 * NOW WITH: LocalStorage caching for offline access and 7-day auto-clearing
 */
import { supabase } from '@/config/supabase';
import { generateArticleId, generateManualArticleId } from '@/utils/idGenerator';
import { fetchVideoIdForArticle } from './youtubeService';
import { LocalStorageSyncService } from './localStorageSyncService';
import { CacheService } from './cacheService';
import type { ArticleData } from '@/types';
export interface ArticleRecord {
  id: string;
  user_id?: string;
  auth_user_id?: string;
  roadmap_id?: string;
  day_id?: string;
  title: string;
  subtitle?: string;
  deep_dive?: string;
  technical_concepts?: any;
  steps?: any;
  example?: string;
  example_type?: 'code' | 'metaphor';
  resources?: any;
  resource_validation?: any;
  quiz?: any;
  topic?: string;
  task?: string;
  language?: string;
  view_count: number;
  created_at: string;
  search_term_en?: string;
}
/**
 * Save or update an article (upsert)
 * If article with same ID exists, it returns existing one
 * Automatically fetches and caches YouTube video ID based on search_term_en
 * Implements fallback searchTerm generation if AI fails
 */
export async function saveArticle(
  article: ArticleData,
  roadmapId?: string,
  userId?: string
): Promise<{ data: ArticleRecord | null; error: any; isNew: boolean }> {
  try {
    console.log('Saving article:', article.id);
    // Use the pre-generated article ID from article object (roadmap_id_day format)
    const id = article.id;
    if (!id) {
      console.error('No article ID provided');
      return { 
        data: null, 
        error: 'Article ID is required', 
        isNew: false 
      };
    }
    // Check if article already exists
    const { data: existing, error: existError } = await supabase
      .from('articles')
      .select('*')
      .eq('id', id)
      .single();
    if (existError && existError.code !== 'PGRST116') {
      console.error('Error checking existing article:', existError);
    }
    if (existing) {
      console.log('Article already exists');
      return { data: existing, error: null, isNew: false };
    }
    console.log('Creating new article...');
    // Extract search term: ALWAYS use article.searchTerm if available (AI-generated, goal-first format)
    // Fallback to technical_concepts or topic if searchTerm is missing
    const searchTerm = article.searchTerm || article.technicalConcepts?.[0]?.term || article.keywords?.[0] || article.topic || undefined;
    console.log('Search term for article:', {
      provided: !!article.searchTerm,
      value: searchTerm,
      source: article.searchTerm ? 'AI-generated (goal-first)' : 'fallback'
    });
    // Prepare clean article data
    const articleData: Partial<ArticleRecord> = {
      id,
      roadmap_id: roadmapId || undefined,
      user_id: userId || undefined,
      title: article.title || article.topic || 'Untitled',
      topic: article.topic || 'General Topic',
      day_id: article.dayId || undefined,
      search_term_en: searchTerm,
      subtitle: article.subtitle || undefined,
      deep_dive: article.deepDive || undefined,
      technical_concepts: article.technicalConcepts || [],
      steps: article.steps || [],
      example: article.example || undefined,
      example_type: article.exampleType || 'code',
      resources: article.resources || {},
      language: article.language || 'en',
      created_at: new Date().toISOString()
    };
    console.log('Inserting article into database...');
    const { data, error } = await supabase
      .from('articles')
      .insert([articleData])
      .select()
      .single();
    if (error) {
      console.error('Article save failed:', error);
      return { data: null, error, isNew: false };
    }
    const savedData = data || null;
    if (savedData) {
      console.log('Article saved successfully');
      
      // Cache article to localStorage for offline access
      const cacheKey = CacheService.generateKey('article', article.id || 'unknown');
      CacheService.set(cacheKey, savedData, 1000 * 60 * 60 * 24 * 7, 'article');
      console.log('✅ Article cached to localStorage');
    }
    return { data: savedData, error: null, isNew: true };
  } catch (error) {
    console.error('Error saving article:', error);
    return { data: null, error, isNew: false };
  }
}
/**
 * Get an article by its structured ID
 * Now using goal_title_day-X format which is language-agnostic
 */
export async function getArticleById(
  id: string
): Promise<{ data: ArticleRecord | null; error: any }> {
  try {
    console.log('🔍 [ArticleService] Looking up article with ID:', id);
    const { data, error } = await supabase
      .from('articles')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) {
      console.error('❌ [ArticleService] Supabase query error:', error);
      return { data: null, error };
    }
    const record = data || null;
    // Increment view count if found
    if (record) {
      console.log('✅ [ArticleService] Article found in database:', {
        id: record.id,
        title: record.title,
        viewCount: record.view_count,
        hasExample: !!record.example,
        exampleLength: record.example?.length || 0,
        exampleType: record.example_type
      });
      // Async increment view count (don't wait)
      (async () => {
        try {
          await supabase
            .from('articles')
            .update({ view_count: (record.view_count || 0) + 1 })
            .eq('id', id);
          console.log('📊 [ArticleService] View count incremented');
        } catch (err) {
          console.warn('⚠️ [ArticleService] Failed to increment view count:', err);
        }
      })();
    } else {
      console.log('ℹ️ [ArticleService] Article not found in database');
    }
    return { data: record, error: null };
  } catch (error) {
    console.error('❌ [ArticleService] Unexpected error:', error);
    return { data: null, error };
  }
}
/**
 * Get articles by roadmap ID
 */
export async function getArticlesByRoadmap(
  roadmapId: string
): Promise<{ data: ArticleRecord[] | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('articles')
      .select('*')
      .eq('roadmap_id', roadmapId)
      .order('created_at', { ascending: true });
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
}
/**
 * Search articles by title and goal only (excluding subtitle)
 * Includes SQL injection protection via proper escaping
 */
export async function searchArticles(
  searchTerm: string,
  limit: number = 20
): Promise<{ data: ArticleRecord[] | null; error: any }> {
  try {
    // Escape the search term to prevent SQL injection
    const escapedTerm = searchTerm.replace(/'/g, "''").replace(/%/g, '\\%').replace(/_/g, '\\_');
    const { data, error } = await supabase
      .from('articles')
      .select('*')
      .or(`title.ilike.%${escapedTerm}%,goal.ilike.%${escapedTerm}%`)
      .order('view_count', { ascending: false })
      .limit(limit);
    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
}
/**
 * Delete an article (only by creator)
 */
export async function deleteArticle(
  id: string
): Promise<{ error: any }> {
  try {
    const { error } = await supabase
      .from('articles')
      .delete()
      .eq('id', id);
    return { error };
  } catch (error) {
    return { error };
  }
}
/**
 * Convert ArticleRecord to ArticleData format for app compatibility
 */
export function recordToArticleData(record: ArticleRecord): ArticleData {
  return {
    id: record.id,
    dayId: record.day_id || '',
    title: record.title,
    subtitle: record.subtitle || '',
    deepDive: record.deep_dive || '',
    technicalConcepts: record.technical_concepts || [],
    steps: record.steps || [],
    example: record.example || '',
    exampleType: record.example_type || 'code',
    resources: record.resources || {},
    quiz: record.quiz || undefined,
    topic: record.topic || '',
    task: record.task || '',
    language: 'en', // Can be extracted from ID if needed
    searchTerm: record.search_term_en || '',
    cachedAt: new Date().toISOString(),
  };
}
